import type { FabricPath, FabricShareInfo, Shape } from './typing.ts';
import {
  DEFAULT_FABRIC_HEIGHT,
  DEFAULT_FABRIC_WIDTH,
  getMMNum,
  isAbsoluteURL,
  isAliyunOSSUrl,
  isStyle3dUrl,
  isBase64Image,
  joinUrl
} from '@repo/utils';
import { reduceToOneColor } from '@repo/opencv';

export function getFabricPathConfigure(shape: Shape): Partial<FabricPath> {
  const fabricPath: Partial<FabricPath> = {};
  if (shape.width && shape.height && shape.dpm) {
    fabricPath.width = getMMNum(shape.dpm.x, shape.width);
    fabricPath.height = getMMNum(shape.dpm.y, shape.height);
  } else {
    fabricPath.width = shape.width || DEFAULT_FABRIC_WIDTH;
    fabricPath.height = shape.height || DEFAULT_FABRIC_HEIGHT;
  }
  if (typeof shape.name === 'string' && /\.png$/i.test(shape.name)) {
    fabricPath.isTransparent = true;
  }
  return fabricPath;
}

export function getFabricPath(shape: Shape, baseUrl: string, resolution: 'sd' | 'hd'): FabricPath {
  let fabricPath: FabricPath = {
    id: shape.id
  };
  const url = isAbsoluteURL(shape.name) || isBase64Image(shape.name)
    ? shape.name
    : joinUrl(baseUrl, `/fabric/${shape.name}`);

  if (shape.name.endsWith('.sfab')) {
    fabricPath.model3dPath = url;
  } else {
    fabricPath.xhrPath = (isAliyunOSSUrl(url) || isStyle3dUrl(url)) && resolution === 'sd' ? `${url}?x-oss-process=image/resize,m_fixed,h_512,w_512,limit_0` : url;
    fabricPath = { ...fabricPath, ...getFabricPathConfigure(shape), path: fabricPath.xhrPath };
  }
  return fabricPath;
}

export function getPieceShapeMap(shapeList: Shape[]) {
  const map = new Map<string, Shape>();
  const validShape = shapeList.filter(shape => !!shape.piece && !!shape.name);
  /**
   * 老的数据shape可能没有id，衣片按照名称匹配
   * 新的数据有id，衣片按照id匹配
   */
  validShape.forEach(shape => {
    map.set(shape.id ? `${shape.id}` : shape.piece, {
      ...shape,
      name: shape.name
    });
  });
  return map;
}

export async function mergeShareFabricInfo(
  shapeList: Shape[],
  projectRoot: string,
  fabricShareInfo: Omit<FabricShareInfo, 'mask' | 'metaData' | 'doc' | 'enterprise_id'>,
  fabric?: {
    type: string,
    id: string,
    originUrl?: string,
    fabricRootPath?: string
  }) {
  const info = fabricShareInfo.info;
  const mapShape = async (shape?: Pick<Shape, 'name' | 'color'>, onlyColor?: boolean) => {
    if (!shape) {
      return {};
    }
    const name = fabric?.fabricRootPath
      ? joinUrl(fabric.fabricRootPath, `/fabric/${shape.name}`)
      : shape.name;
    let color = shape.color;

    if (onlyColor && !color) {
      const url = isAliyunOSSUrl(name) || isStyle3dUrl(name) ? `${name}?x-oss-process=image/resize,m_fixed,h_128,w_128,limit_0` : name;
      color = await reduceToOneColor(url);
    }
    return {
      ...shape,
      name,
      color
    };
  };
  //// materialIndex非0且为主料不需要合并
  //const noMergeNeededPredicate = (item: Shape) => !!item?.materialIndex;
  const noMergeNeededPredicate = (item: Shape) => false;
  const mergedShapeList = shapeList.filter(item => !noMergeNeededPredicate(item))
      .map(async item => {
      switch (item.type) {
        case 'hem':
        case 'collar':
        case 'placket':
          return {
            ...item,
            ...(await mapShape(info[item.type] || info['hem'], item.onlyColor)),
            fabric
          };
        default:
          return {
            ...item,
            ...(await mapShape(info['main'])),
            fabric
          };
      }
    });

  const unMergedShapeList = shapeList
    .filter(noMergeNeededPredicate)
    .filter(item => item.name)
    .map(item => ({
      ...item,
      name: `${projectRoot}/fabric/${item.name}`
    }));

  return [unMergedShapeList, await Promise.all(mergedShapeList)].flat();
}
